/**
 * ENG-10335 — "is this session blocked on a prompt that nothing can answer?"
 *
 * `isSessionHealthy()` answers a narrower question: does the `agt-<codeName>`
 * tmux session exist, and is a claude process alive inside it. A session parked
 * on Claude Code's login picker passes both checks, so it reads healthy, and
 * three consumers act on that reading:
 *
 *   - the per-tick `[persistent-session-decision]` verdict
 *     (`session_healthy_after=true`), which the spawn-outcome map publishes to
 *     the console heartbeat;
 *   - scheduled-task firing, which materialises a card and nudges the session;
 *   - the direct-chat doorbell, which tells an in-session MCP to pull.
 *
 * On agt-aws-1 (2026-09-10) marlow and then phil respawned onto the picker and
 * all three consumers kept treating them as live, 12ms after the reaper had
 * logged that neither agent could run a single tool call.
 *
 * `isSessionHealthy()` is deliberately NOT changed. Many of its callers treat
 * `false` as "respawn it", and a respawn re-presents the same prompt, which is
 * the restart loop ENG-9932 exists to prevent. This module is a separate,
 * narrower predicate that those three consumers consult alongside it.
 *
 * ## Who marks an agent, and why that keeps it cheap
 *
 * Two components already SEE the blocked pane, and before this module recorded
 * it nowhere anyone else could read:
 *
 *   - the spawn-time `acceptDialogs()` cascade in persistent-session.ts, which
 *     logs `CLAUDE LOGIN REQUIRED` about two seconds after spawn;
 *   - the mcp-presence-reaper's ENG-9932 hold (`onPaneBlocked`).
 *
 * They mark the agent as a SUSPECT here. Only a suspect agent ever pays for a
 * pane read, so for a healthy fleet every question is a single Map lookup.
 *
 * ## Fail open, and self-clearing
 *
 * A suspect verdict is trusted for `BLOCKED_PROMPT_VERDICT_TTL_MS`, then
 * re-verified from the live pane with the reaper's own classifier. An unreadable
 * pane, a throwing capture, or a pane that no longer classifies as unanswerable
 * CLEARS the suspicion. That direction is deliberate: a wrong "blocked" hides a
 * live agent and suppresses its work, which is worse than the status quo. It also
 * means nothing has to remember to clear the mark when an operator pairs the host
 * long after `acceptDialogs()` has stopped polling.
 *
 * ## The re-verification never blocks the manager's event loop
 *
 * The questions are asked synchronously, from the per-tick verdict, scheduled
 * firing and direct-chat delivery. A pane capture is a child process with a 2s
 * bound, and the incident this module exists for is host-wide: every agent on
 * agt-aws-1 lands on the picker at once, so every one of them is a suspect at
 * once. Capturing synchronously there would stack N × 2s of stall onto exactly
 * the ticks that most need to run (CodeRabbit on PR #5885).
 *
 * So a stale verdict starts ONE asynchronous capture per agent (deduplicated
 * while it is in flight) and the question is answered from the cached verdict in
 * the meantime. The capture's result lands on the NEXT question. The cost is that
 * an agent an operator has just paired stays held for one extra question, which
 * is one poll interval at most; the TTL comment below already budgets for that.
 *
 * A capture that completes after the agent was re-sighted or cleared is
 * discarded, so a slow failed read can never clear a sighting newer than itself.
 *
 * Nothing here returns or logs pane text. The classifier yields a kind, and that
 * is all that leaves this module (see summarizeUnanswerablePane, PR #5504).
 */

import { execFile } from 'node:child_process';
import { classifyUnanswerablePane, type UnanswerablePaneKind } from './claude-dialogs.js';

/** Which component saw the prompt. Kept for diagnostics only; nothing branches on it. */
export type BlockedPromptSource = 'spawn-dialogs' | 'mcp-presence-reaper';

/**
 * How long a sighting is trusted before the live pane is read again.
 *
 * Short enough that an agent an operator has just paired is routed work again
 * within one or two poll intervals; long enough that the several consumers that
 * ask within one manager tick share a single capture.
 */
export const BLOCKED_PROMPT_VERDICT_TTL_MS = 15_000;

/** The decision string published when a healthy-looking session is blocked. */
export const BLOCKED_ON_PROMPT_DECISION = 'blocked-on-prompt' as const;

/**
 * Reads the agent's tmux pane, or yields null when it cannot. Production resolves
 * asynchronously; a plain return value is accepted so tests can inject a pane.
 */
export type PaneReader = (codeName: string) => Promise<string | null> | string | null;

interface SuspectEntry {
  kind: UnanswerablePaneKind;
  source: BlockedPromptSource;
  verifiedAt: number;
  /** Surfaces that have already logged this episode, so a held task does not log every tick. */
  loggedSurfaces: Set<string>;
  /** The in-flight re-verification, so every stale question in the meantime shares one capture. */
  refresh?: Promise<UnanswerablePaneKind | null>;
}

const suspects = new Map<string, SuspectEntry>();

/**
 * The production pane reader. Same tmux invocation and 2s bound as the reaper's
 * `readPane` in manager-worker.ts, but asynchronous (see the header), and the
 * same swallow-to-null: a tmux hiccup is not evidence of a blocked pane.
 */
export const readAgentPane: PaneReader = (codeName) =>
  new Promise((resolve) => {
    execFile('tmux', ['capture-pane', '-t', `agt-${codeName}`, '-p'], { timeout: 2_000 }, (err, stdout) => {
      resolve(err ? null : String(stdout));
    });
  });

/**
 * Record that `codeName`'s session was just seen blocked on `kind`.
 *
 * A sighting counts as a verification: the caller has just read the pane, so the
 * verdict is fresh as of `now`. Re-noting the same kind keeps the episode's
 * logged-surface memory; a different kind starts a new episode.
 */
export function noteSessionBlockedOnPrompt(
  codeName: string,
  kind: UnanswerablePaneKind,
  source: BlockedPromptSource,
  now: number = Date.now(),
): void {
  const existing = suspects.get(codeName);
  suspects.set(codeName, {
    kind,
    source,
    verifiedAt: now,
    loggedSurfaces: existing && existing.kind === kind ? existing.loggedSurfaces : new Set(),
  });
}

/** Forget any suspicion for `codeName` (a fresh spawn, a stop, a session that came up). */
export function clearSessionBlockedOnPrompt(codeName: string): boolean {
  return suspects.delete(codeName);
}

export interface BlockedPromptQueryOptions {
  /** Injected for tests. Defaults to a bounded `tmux capture-pane`. */
  readPane?: PaneReader;
  /** Injected for tests. Defaults to `Date.now()`. */
  now?: number;
}

/**
 * The kind of unanswerable prompt `codeName`'s session is blocked on, or null.
 *
 * Synchronous and never reads a pane itself. Null for any agent nobody has
 * marked. For a marked agent the cached kind is returned; if that verdict is older
 * than the TTL, a re-verification is started in the background and its result
 * (including a fail-open clear) answers the next question.
 */
export function sessionBlockedOnPrompt(
  codeName: string,
  options: BlockedPromptQueryOptions = {},
): UnanswerablePaneKind | null {
  const entry = suspects.get(codeName);
  if (!entry) return null;

  const now = options.now ?? Date.now();
  if (now - entry.verifiedAt >= BLOCKED_PROMPT_VERDICT_TTL_MS) {
    void refreshSessionBlockedOnPrompt(codeName, { ...options, now });
  }
  return entry.kind;
}

/**
 * Re-verify `codeName`'s mark from the live pane and resolve the resulting
 * verdict. Deduplicated: while a capture is in flight every caller gets that same
 * promise. Exported so tests (and any caller that genuinely needs the fresh
 * answer) can await it; the manager's consumers go through sessionBlockedOnPrompt.
 *
 * Anything short of a positive classification clears the mark: a capture that
 * resolves null, rejects, or throws synchronously is not evidence of a prompt. A
 * capture that completes after the agent was re-sighted or cleared is discarded,
 * and the current state is resolved instead.
 */
export function refreshSessionBlockedOnPrompt(
  codeName: string,
  options: BlockedPromptQueryOptions = {},
): Promise<UnanswerablePaneKind | null> {
  const entry = suspects.get(codeName);
  if (!entry) return Promise.resolve(null);
  if (entry.refresh) return entry.refresh;

  const startedAt = options.now ?? Date.now();
  const readPane = options.readPane ?? readAgentPane;
  let capture: Promise<string | null>;
  try {
    capture = Promise.resolve(readPane(codeName));
  } catch {
    capture = Promise.resolve(null);
  }

  const refresh = capture
    .catch(() => null)
    .then((pane): UnanswerablePaneKind | null => {
      entry.refresh = undefined;
      // Re-noted (a new object) or cleared while the capture ran: that is newer
      // than this read, so it wins.
      if (suspects.get(codeName) !== entry) return suspects.get(codeName)?.kind ?? null;

      const kind = classifyUnanswerablePane(pane);
      if (!kind) {
        suspects.delete(codeName);
        return null;
      }
      if (kind !== entry.kind) entry.loggedSurfaces = new Set();
      entry.kind = kind;
      entry.verifiedAt = startedAt;
      return kind;
    });
  entry.refresh = refresh;
  return refresh;
}

/**
 * Health for a consumer that needs the session to actually run tool calls: the
 * usual `isSessionHealthy()` AND not blocked on a prompt.
 *
 * `isHealthy` is ALWAYS called, and called first. In manager-worker.ts it hydrates
 * a newly discovered tmux session into the session Map, and callers such as
 * verifyPendingRestarts read that Map straight afterwards; a blocked-first short
 * circuit would silently change what they read.
 */
export function sessionHealthyAndUnblocked(
  codeName: string,
  isHealthy: (codeName: string) => boolean,
  blockedOn: (codeName: string) => UnanswerablePaneKind | null = sessionBlockedOnPrompt,
): boolean {
  const healthy = isHealthy(codeName);
  return healthy && blockedOn(codeName) === null;
}

/**
 * Edge-trigger for log lines. True the first time `surface` asks about a
 * currently-marked agent in this episode, false after that and for unmarked
 * agents. A deferred task stays ready and is re-evaluated every tick, so logging
 * on the level would repeat for as long as the prompt stays up.
 */
export function shouldLogBlockedPrompt(codeName: string, surface: string): boolean {
  const entry = suspects.get(codeName);
  if (!entry || entry.loggedSurfaces.has(surface)) return false;
  entry.loggedSurfaces.add(surface);
  return true;
}

export interface SessionVerdict<D extends string = string> {
  decision: D;
  /**
   * Set only on a verdict corrected to `blocked-on-prompt`: the decision the tick
   * actually made. Structured (not just in `detail`) because the fast lanes
   * branch on it, see isConfirmedRespawn.
   */
  underlyingDecision?: string;
  spawnAttempted: boolean;
  sessionHealthyAfter: boolean;
  detail?: string;
}

/**
 * Correct a per-tick session verdict for a blocked prompt.
 *
 * Only a HEALTHY verdict can be wrong in this direction, so an unhealthy one is
 * returned untouched and keeps its own decision. A healthy verdict on a blocked
 * session becomes unhealthy, with the underlying decision kept in `detail` so the
 * log line still says what the tick actually did.
 *
 * Downstream effects of `sessionHealthyAfter=false` with no spawn attempt, all
 * intended: bind remediation is skipped (a forced respawn cannot answer the
 * prompt), the console heartbeat stops reporting the agent healthy, and the
 * ENG-5116 stuck tracker WARNs after three ticks. None of them restarts the
 * session.
 */
export function applyBlockedPromptVerdict<D extends string>(
  result: SessionVerdict<D>,
  blocked: UnanswerablePaneKind | null,
): SessionVerdict<D | typeof BLOCKED_ON_PROMPT_DECISION> {
  if (!blocked || !result.sessionHealthyAfter) return result;
  return {
    ...result,
    decision: BLOCKED_ON_PROMPT_DECISION,
    underlyingDecision: result.decision,
    sessionHealthyAfter: false,
    detail:
      `session blocked on an interactive prompt (${blocked}) and cannot run a tool call; ` +
      `underlying decision=${result.decision}` +
      (result.detail ? `; ${result.detail}` : ''),
  };
}

/**
 * Did a fast-lane respawn happen, for the purpose of acking the restart request?
 *
 * The two fast respawn lanes (the restart doorbell and the post-MCP-stop respawn)
 * ack only a confirmed respawn and otherwise leave `restart_requested_at` set, so
 * the slow poll kills and respawns the agent again. Before ENG-10335 a respawn
 * that landed on the login picker read healthy and was acked. Once the verdict is
 * corrected to `blocked-on-prompt` it would read as NOT confirmed, and the slow
 * poll would restart the agent a second time, into the same prompt: the futile
 * restart ENG-9932 exists to prevent.
 *
 * So a corrected verdict counts as confirmed when the tick underneath it really
 * did spawn. The restart happened; what it landed on is published as unhealthy,
 * and a further restart cannot answer the prompt. Every other verdict keeps the
 * lanes' original rule exactly: decision `spawn` and healthy afterwards.
 */
export function isConfirmedRespawn(result: SessionVerdict): boolean {
  if (result.decision === BLOCKED_ON_PROMPT_DECISION) return result.underlyingDecision === 'spawn';
  return result.decision === 'spawn' && result.sessionHealthyAfter;
}

/** Test helper: drop all state. */
export function __resetBlockedPromptStateForTests(): void {
  suspects.clear();
}
